import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadExcel, calcularPlano, salvarPlano } from '../api';
import './ListaVolumes.css';

const STATUS_LABEL = {
  pendente:    'Pendente',
  calculado:   'Calculado',
  editado:     'Editado',
  gerado_pdf:  'PDF Gerado',
  erro:        'Erro',
};

const STATUS_CLASS = {
  pendente:   'pal-status-pendente',
  calculado:  'pal-status-calculado',
  editado:    'pal-status-editado',
  gerado_pdf: 'pal-status-pdf',
  erro:       'pal-status-erro',
};

// ── Upload Dropzone ───────────────────────────────────────────────────────────

function UploadDropzone({ onJobCarregado }) {
  const [loading, setLoading] = useState(false);
  const [erro, setErro]       = useState(null);

  const handleUpload = useCallback(async (file) => {
    setLoading(true);
    setErro(null);
    try {
      const data = await uploadExcel(file);
      onJobCarregado(data);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  }, [onJobCarregado]);

  const onDrop = useCallback((accepted) => {
    if (!accepted.length) return;
    const file = accepted[0];
    const ext  = file.name.split('.').pop().toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xls') {
      setErro('Apenas arquivos .xlsx ou .xls são aceitos.');
      return;
    }
    handleUpload(file);
  }, [handleUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  return (
    <div className="pal-upload-section">
      <div
        {...getRootProps()}
        className={`pal-dropzone${isDragActive ? ' active' : ''}${loading ? ' loading' : ''}`}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="pal-dropzone-content">
            <span className="pal-spinner" />
            <p>Enviando arquivo…</p>
          </div>
        ) : (
          <div className="pal-dropzone-content">
            <span className="pal-upload-icon"></span>
            <p className="pal-upload-text">
              {isDragActive
                ? 'Solte o arquivo aqui…'
                : 'Arraste o .xlsx do Senior ou clique para selecionar'}
            </p>
            <p className="pal-upload-hint">Planilha de embalagem exportada do ERP Senior</p>
          </div>
        )}
      </div>

      {erro && (
        <div className="pal-error">
          <strong>Erro</strong>
          <p>{erro}</p>
        </div>
      )}
    </div>
  );
}

// ── Tabela de Volumes ─────────────────────────────────────────────────────────

function TabelaVolumes({ volumes, calculandoIds, onCalcular, onCalcularTudo }) {
  const numPendentes = volumes.filter(v => v.status === 'pendente').length;

  return (
    <div className="pal-tabela-section">
      <div className="pal-tabela-topbar">
        <span className="pal-tabela-count">
          {volumes.length} volume{volumes.length !== 1 ? 's' : ''}
        </span>
        {numPendentes > 0 && (
          <button className="pal-btn-primary" onClick={onCalcularTudo}>
            Calcular Volumes ({numPendentes})
          </button>
        )}
      </div>

      <div className="pal-tabela-wrapper">
        <table className="pal-tabela">
          <thead>
            <tr>
              <th>Linha</th>
              <th>Produto</th>
              <th>Descrição</th>
              <th>C × L × A (mm)</th>
              <th>Peso (kg)</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {volumes.map(vol => {
              const calculando = calculandoIds.has(vol.id);
              return (
                <tr key={vol.id} className={vol.status === 'erro' ? 'pal-row-erro' : ''}>
                  <td className="pal-col-linha">{vol.linha_excel}</td>
                  <td className="pal-col-produto">{vol.produto}</td>
                  <td className="pal-col-descr">{vol.descricao || '—'}</td>
                  <td className="pal-col-dims">
                    {vol.comprimento_mm} × {vol.largura_mm} × {vol.altura_mm}
                  </td>
                  <td className="pal-col-peso">{vol.peso_bruto_kg}</td>
                  <td>
                    <span className={`pal-status-badge ${STATUS_CLASS[vol.status] || ''}`}>
                      {STATUS_LABEL[vol.status] || vol.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="pal-btn-acao"
                      onClick={() => onCalcular(vol)}
                      disabled={calculando}
                    >
                      {calculando
                        ? <><span className="pal-spinner-sm" /> Calculando…</>
                        : 'Editar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Componente raiz ───────────────────────────────────────────────────────────

function ListaVolumes({ modalidade, job, volumes, onJobCarregado, onAtualizarVolumeStatus, onIrParaDetalhe, onNovoUpload, codigo81, onCodigo81Change }) {
  const [calculandoIds, setCalculandoIds] = useState(new Set());
  const [erroGlobal, setErroGlobal]       = useState(null);

  const addCalculando    = (id) => setCalculandoIds(prev => new Set([...prev, id]));
  const removeCalculando = (id) => setCalculandoIds(prev => { const s = new Set(prev); s.delete(id); return s; });

  const handleCalcularVolume = async (vol) => {
    addCalculando(vol.id);
    setErroGlobal(null);
    try {
      const result = await calcularPlano(vol.id, modalidade);
      if (!result.viavel) {
        onAtualizarVolumeStatus(vol.id, 'erro');
        setErroGlobal(`"${vol.produto}" não paletizável: ${result.mensagem || 'dimensões excedem os limites.'}`);
        return;
      }
      onIrParaDetalhe(vol, result.plano);
    } catch (err) {
      onAtualizarVolumeStatus(vol.id, 'erro');
      setErroGlobal(err.message);
    } finally {
      removeCalculando(vol.id);
    }
  };

  const handleCalcularTudo = async () => {
    const pendentes = volumes.filter(v => v.status === 'pendente');
    if (!pendentes.length) return;
    setErroGlobal(null);

    const queue = [...pendentes];

    const processarProximo = async () => {
      const vol = queue.shift();
      if (!vol) return;

      addCalculando(vol.id);
      try {
        const result = await calcularPlano(vol.id, modalidade);
        if (result.viavel && result.plano) {
          await salvarPlano(vol.id, {
            caixas_por_camada:    result.plano.caixas_por_camada,
            num_camadas:          result.plano.num_camadas,
            layout_orientacao:    result.plano.layout_orientacao,
            caixas_alinhadas:     result.plano.caixas_alinhadas,
            caixas_giradas:       result.plano.caixas_giradas,
            transbordo_girada_mm: result.plano.transbordo_girada_mm,
            sobreposicao_mm:      result.plano.sobreposicao_mm,
          }, modalidade);
          onAtualizarVolumeStatus(vol.id, 'calculado');
        } else {
          onAtualizarVolumeStatus(vol.id, 'erro');
        }
      } catch {
        onAtualizarVolumeStatus(vol.id, 'erro');
      } finally {
        removeCalculando(vol.id);
        if (queue.length > 0) await processarProximo();
      }
    };

    // Até 4 workers concorrentes
    await Promise.all(
      Array.from({ length: Math.min(4, pendentes.length) }, () => processarProximo())
    );
  };

  return (
    <div className="pal-lista">
      <div className="pal-lista-header">
        <h2>Plano de Paletização</h2>
        <p>Faça upload da planilha de embalagem exportada do ERP Senior para calcular os planos de palete.</p>
      </div>

      {!job && <UploadDropzone onJobCarregado={onJobCarregado} />}

      {job && (
        <>
          <div className="pal-job-bar">
            <span className="pal-job-id">Job: <strong>{job.job_id?.slice(0, 8)}…</strong></span>
            <span>{job.total_volumes} volume{job.total_volumes !== 1 ? 's' : ''}</span>
            {/* POC temporário (jul/2026) — código 81 manual do lote; substituir por vínculo ao produto cadastrado no futuro */}
            <label className="pal-codigo81-label">
              Código 81 (POC)
              <input
                type="text"
                className="pal-codigo81-input"
                value={codigo81}
                onChange={(e) => onCodigo81Change(e.target.value)}
                placeholder="810000000 (padrão se vazio)"
              />
            </label>
            <button className="pal-btn-ghost" onClick={onNovoUpload}>
              + Novo upload
            </button>
          </div>

          {job.erros_parse?.length > 0 && (
            <div className="pal-banner-aviso">
              <strong>{job.erros_parse.length} linha{job.erros_parse.length !== 1 ? 's' : ''} com problema no parser:</strong>
              <ul>
                {job.erros_parse.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {erroGlobal && (
            <div className="pal-error pal-error-inline">
              <strong>Erro</strong>
              <p>{erroGlobal}</p>
            </div>
          )}

          <TabelaVolumes
            volumes={volumes}
            calculandoIds={calculandoIds}
            onCalcular={handleCalcularVolume}
            onCalcularTudo={handleCalcularTudo}
            onIrParaDetalhe={onIrParaDetalhe}
          />
        </>
      )}
    </div>
  );
}

export default ListaVolumes;
